import { Component } from '@angular/core';
import { HeaderComponent } from "../../components/header/header.component";
import { ButtonComponent } from "../../components/button/button.component";
import { FormSectionComponent } from "../../components/form-section/form-section.component";
import { SelectInputComponent } from "../../components/select-input/select-input.component";
import { DateInputComponent } from "../../components/date-input/date-input.component";
import { TextInputComponent } from "../../components/text-input/text-input.component";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { GlobalService } from '../../services/global.service';
import { AssessmentService } from '../../services/assessment.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DropdownService } from '../../services/dropdown.service';
import { TimeInputComponent } from '../../components/time-input/time-input.component';
import { FileUploaderComponent } from '../../components/file-uploader/file-uploader.component';
import { NgIf, CommonModule } from '@angular/common';
import { RadioComponent } from '../../components/radio/radio.component';
import { FormService } from '../../services/form.service';
import { ValidationService } from '../../services/validation.service';
import { SubjectService } from '../../services/subject.service';

@Component({
  selector: 'app-assessment-form-page',
  imports: [HeaderComponent, ButtonComponent, FormSectionComponent, TimeInputComponent, SelectInputComponent, DateInputComponent, TextInputComponent, ReactiveFormsModule, FileUploaderComponent, NgIf, CommonModule, RadioComponent],
  templateUrl: './assessment-form-page.component.html',
  styleUrl: './assessment-form-page.component.css'
})
export class AssessmentFormPageComponent {
  page_name = 'Add Assessment';
  form: FormGroup;
  params: any = {};
  selected_ids: any = {}
  items: any = [];
  currentDate: string = new Date(Date.now()).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  directory = '';
  directory_copied = false;
  available_subject_list: any[] = [];
  filtered_exam_list: any[] = [];
  filtered_exam_code_list: any[] = [];
  used_subject_ids: any[] = [];
  // Snapshot of the raw form value right after an existing assessment finishes loading — lets
  // submit() detect a true no-op update (nothing changed) and skip client-side validation that
  // older/legacy data might not satisfy, mirroring the backend's own "nothing changed → skip
  // validation" short-circuit (see update-assessment.function.ts).
  private initial_form_snapshot: string | null = null;
  // Resolves when an existing assessment has finished loading into the form. ngOnInit awaits it
  // before deriving anything that needs both the patched values and the dropdown lists.
  private patch_promise: Promise<void> | null = null;
  enable_disable_options = [
    { label: 'Enable', value: 'enable' },
    { label: 'Disable', value: 'disable' },
  ];
  // Set from the get-assessment response on edit. The backend refuses to update the AI Features
  // fields once any script is in play — see update-assessment.function.ts (blanket evaluation
  // guard, restricted_fields for `type`, and the AI Features toggle guard). Reading the flag the
  // backend computes keeps the two in sync and stops the admin from filling in changes that can
  // only fail on Save.
  is_ai_features_locked = false;

  get selected_university(): any {
    const university_id = this.form.get('university_id')?.value;
    return this.ds.dropdown_list?.university_list?.find((x: any) => x.value == university_id);
  }

  get show_image_to_text(): boolean {
    return this.selected_university?.image_to_text === 'enable';
  }

  get show_ai_proctoring(): boolean {
    return this.selected_university?.ai_proctoring === 'enable';
  }

  get show_ai_evaluation(): boolean {
    return this.selected_university?.ai_evaluation === 'enable';
  }

  get model_answer_sheet_required(): boolean {
    return this.form.get('ai_evaluation')?.value === 'enable' || this.form.get('no_of_evaluation')?.value === 'AI';
  }

  get is_ai_only(): boolean {
    return this.form.get('no_of_evaluation')?.value === 'AI';
  }

  all_evaluation_level_options = [
    { title: 'AI Only', value: 'AI' }, { title: '1', value: '1' }, { title: '2', value: '2' },
    { title: '3', value: '3' }, { title: '4', value: '4' }, { title: '5', value: '5' },
    { title: '6', value: '6' }, { title: '7', value: '7' }, { title: '8', value: '8' },
    { title: '9', value: '9' }, { title: '10', value: '10' },
  ];

  // Pre-computed once so the getter below always hands back a stable array reference.
  // Filtering inline created a new array on every change detection cycle, which made
  // app-select-input's ngOnChanges fire nonstop and wipe the user's selection.
  evaluation_level_options_without_ai = this.all_evaluation_level_options.filter(o => o.value !== 'AI');

  // AI Only relies entirely on AI scoring — hide it once AI Evaluation is off so the admin
  // can't pick an evaluation level that has no way to actually get scored.
  get evaluation_level_options() {
    return this.form.get('ai_evaluation')?.value === 'enable' ? this.all_evaluation_level_options : this.evaluation_level_options_without_ai;
  }

  // Assessment Type is capped by the selected university's Access Control setting
  // (University.assessment_type: 'auto' | 'manual' | 'both'). Stable array references for the
  // same reason as evaluation_level_options_without_ai above — a fresh array per change
  // detection cycle makes the options @Input churn needlessly.
  private assessment_type_auto_only = [{ label: 'Auto', value: 'Auto' }];
  private assessment_type_manual_only = [{ label: 'Manual', value: 'Manual' }];
  private assessment_type_both = [{ label: 'Auto', value: 'Auto' }, { label: 'Manual', value: 'Manual' }];
  // Flips once ngOnInit has settled both the dropdown lists and any patched record, so the
  // university_id subscription can tell a real user switch from the initial load.
  private initial_load_done = false;

  // What the university's Access Control setting permits, ignoring what's currently stored.
  private get allowed_assessment_types() {
    // No university picked yet (or the column is unset) → no constraint to apply, offer both.
    switch (String(this.selected_university?.assessment_type || '').toLowerCase()) {
      case 'auto': return this.assessment_type_auto_only;
      case 'manual': return this.assessment_type_manual_only;
      default: return this.assessment_type_both;
    }
  }

  get assessment_type_options() {
    const allowed = this.allowed_assessment_types;
    const current = this.form.get('type')?.value;
    // Assessments saved before the university was narrowed can hold a type it no longer allows.
    // Show that value rather than rendering the radio blank — the restriction governs what can
    // be picked from here on, it must not misreport what is actually stored.
    return current && !allowed.some((o) => o.value === current) ? this.assessment_type_both : allowed;
  }

  /**
   * Keeps `type` consistent with what the selected university permits. Same idea as the
   * ai_evaluation → no_of_evaluation reset in the constructor: an option that just disappeared
   * must not stay selected behind the scenes.
   *
   * `correct_existing` separates the two cases that reach here:
   * - initial load (false) — never rewrite a stored value. Legacy assessments predate their
   *   university's current setting, and silently flipping them would turn an unrelated edit into
   *   a data change the admin never asked for.
   * - admin switched university (true) — the old value is genuinely stale, so replace it.
   *
   * Skipped once AI Features are locked: the backend refuses to change `type` at that point
   * (update-assessment.function.ts restricted_fields), so rewriting it would only guarantee a
   * failed save. Also skipped in view mode, which must show what was actually saved.
   */
  sync_assessment_type(correct_existing = false) {
    if (this.is_ai_features_locked || this.params.action == 'view') return;
    const allowed = this.allowed_assessment_types;
    const type_ctrl = this.form.get('type');
    if (allowed.some((o) => o.value === type_ctrl?.value)) return;
    if (!type_ctrl?.value) {
      // New assessment — pre-select when the university leaves only one choice.
      if (allowed.length === 1) type_ctrl?.setValue(allowed[0].value);
      return;
    }
    if (correct_existing) type_ctrl.setValue(allowed.length === 1 ? allowed[0].value : '');
  }

  moderation_range_validator(group: AbstractControl): ValidationErrors | null {
    const low_enabled = group.get('low_score_enabled')?.value;
    const high_enabled = group.get('high_score_enabled')?.value;
    if (!low_enabled || !high_enabled) return null;
    const low = Number(group.get('low_score_marks')?.value);
    const high = Number(group.get('high_score_marks')?.value);
    if (group.get('low_score_marks')?.value == null || group.get('high_score_marks')?.value == null) return null;
    return high < low ? { markRangeInvalid: true } : null;
  }

  notZeroTimeValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value === '00:00:00') {
      return { zeroTime: true };
    }
    return null;
  }

  constructor(public gs: GlobalService, public route: Router, public fb: FormBuilder, public as: AssessmentService, public ar: ActivatedRoute, public ds: DropdownService, public fs: FormService, public vs: ValidationService, public ss: SubjectService) {
    this.form = this.fb.group({
      exam_id: ['', [Validators.required]],
      academic_year_id: ['', [Validators.required]],
      university_id: ['', [Validators.required]],
      college_id: ['', [Validators.required]],
      level_id: ['', [Validators.required]],
      program_id: ['', [Validators.required]],
      stream_id: ['', [Validators.required]],
      total_marks: [1, [Validators.required, Validators.maxLength(3)]],
      subject_id: ['', [Validators.required]],
      passing_marks: [1, [Validators.required, Validators.min(0), this.fs.compareControls('total_marks', 'lessThanOrEqual', 'passingMarksExceedTotal', 'Passing marks cannot be greater than total marks', () => this.form)]],
      evaluation_start_date: ['', [Validators.required,]],
      evaluation_end_date: ['', [Validators.required]],
      per_day_target: ['', [Validators.required, Validators.min(1)]],
      grace_period: ['', [Validators.required, this.notZeroTimeValidator.bind(this)]],
      min_time_per_answer_script: ['', [Validators.required, this.notZeroTimeValidator.bind(this)]],
      annotations_required: [false, [Validators.required]],
      no_of_evaluation: ['', [Validators.required]],
      head_examiner_required: [false, [Validators.required]],
      script_he_to_check: ['', [Validators.min(1), Validators.max(100)]],
      moderation_type: ['Default'],
      moderation_config: this.fb.group({
        low_score_enabled: [false],
        low_score_marks: [null],
        high_score_enabled: [false],
        high_score_marks: [null],
        variance_enabled: [false],
        variance_marks: [null],
        question_variance_enabled: [false],
        question_variance_marks: [null],
      }),
      type: ['', [Validators.required]],
      // re-evaluation fields
      re_evaluation_required: [false, [Validators.required]],
      re_evaluation_start_date: [''],
      re_evaluation_end_date: [''],
      re_evaluation_last_date: [''],
      re_evaluation_fees: [false],
      amount: [+''],
      no_of_re_evaluations: [1],
      re_evaluation_he_required: [false],
      re_evaluation_script_he_to_check: [''],
      re_evaluation_moderation_type: ['Default'],
      re_evaluation_moderation_config: this.fb.group({
        low_score_enabled: [false],
        low_score_marks: [null],
        high_score_enabled: [false],
        high_score_marks: [null],
        variance_enabled: [false],
        variance_marks: [null],
        question_variance_enabled: [false],
        question_variance_marks: [null],
      }),
      qp_pattern: ['', [Validators.required]],
      qp_file: ['', [Validators.required]],
      soe_upload: ['',],
      // Required only when AI Evaluation is enabled — see update_model_answer_sheet_validator.
      model_answer_sheet: [''],
      rubrics_json: this.fb.array([]),
      // Script config fields
      no_of_pages_per_script: ['', [Validators.required, Validators.pattern(this.vs.code_pattern), Validators.min(1)]],
      hide_script_pages_for_faculty: ['', [Validators.required, Validators.pattern(this.vs.code_pattern), this.fs.compareControls('no_of_pages_per_script', 'lessThanOrEqual', 'hideScriptPagesExceedTotal', 'Pages to hide cannot exceed total pages per script', () => this.form)]],
      hide_script_percentage: ['', [Validators.required, Validators.pattern(/^[0-9%]*$/), Validators.pattern(/^(0%?|([1-9][0-9]?|100)%?)$/)]],
      no_of_pages_per_supplement: ['', [Validators.required]],
      hide_supplement_pages_for_faculty: ['', [Validators.required, Validators.pattern(/^[0-9%]*$/), Validators.pattern(/^(0%?|([1-9][0-9]?|100)%?)$/), this.fs.compareControls('no_of_pages_per_supplement', 'lessThanOrEqual', 'hideSupplementPagesExceedTotal', 'Pages to hide cannot exceed total pages per supplement', () => this.form)]],
      hide_supplement_percentage: ['', [Validators.required, Validators.pattern(/^[0-9%]*$/), Validators.pattern(/^(0%?|([1-9][0-9]?|100)%?)$/)]],
      image_to_text: ['disable'],
      ai_proctoring: ['disable'],
      ai_evaluation: ['disable'],
    });

    this.form.get('head_examiner_required')?.valueChanges.subscribe((value) => {
      this.he_is_required(value);
    });
    this.form.get('moderation_type')?.valueChanges.subscribe((value) => {
      this.on_moderation_type_change(value);
    });
    const cfg = this.form.get('moderation_config') as FormGroup;
    cfg.get('low_score_enabled')?.valueChanges.subscribe(() => this.update_condition_validators());
    cfg.get('high_score_enabled')?.valueChanges.subscribe(() => this.update_condition_validators());
    cfg.get('variance_enabled')?.valueChanges.subscribe(() => this.update_condition_validators());
    cfg.get('question_variance_enabled')?.valueChanges.subscribe(() => this.update_condition_validators());
    this.form.get('no_of_evaluation')?.valueChanges.subscribe(() => {
      this.update_variance_state();
      this.update_model_answer_sheet_validator();
      this.update_ai_only_field_validators();
    });
    this.form.get('re_evaluation_required')?.valueChanges.subscribe((value) => {
      this.updateReEvaluationValidators(value);
    });

    this.form.get('ai_evaluation')?.valueChanges.subscribe((value) => {
      this.update_model_answer_sheet_validator();
      this.update_variance_state();
      this.update_re_eval_variance_state();
      // AI Only option just got hidden — the previously selected value is no longer valid.
      if (value !== 'enable' && this.form.get('no_of_evaluation')?.value === 'AI') {
        this.form.get('no_of_evaluation')?.setValue('');
      }
    });

    // Switching university can narrow the allowed Assessment Types — re-check the current pick.
    // This also fires while patch_form_data() loads an existing record, hence the flag: only a
    // real post-load switch may overwrite a stored value.
    this.form.get('university_id')?.valueChanges.subscribe(() => {
      this.sync_assessment_type(this.initial_load_done);
    });

    this.form.get('re_evaluation_fees')?.valueChanges.subscribe((value) => {
      this.updateAmountValidator(value);
    });

    this.form.get('re_evaluation_he_required')?.valueChanges.subscribe((value) => {
      this.updateReEvalHeValidator(value);
    });
    this.form.get('re_evaluation_moderation_type')?.valueChanges.subscribe((value) => {
      this.on_re_eval_moderation_type_change(value);
    });
    const re_cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    re_cfg.get('low_score_enabled')?.valueChanges.subscribe(() => this.update_re_eval_condition_validators());
    re_cfg.get('high_score_enabled')?.valueChanges.subscribe(() => this.update_re_eval_condition_validators());
    re_cfg.get('variance_enabled')?.valueChanges.subscribe(() => this.update_re_eval_condition_validators());
    re_cfg.get('question_variance_enabled')?.valueChanges.subscribe(() => this.update_re_eval_condition_validators());
    this.form.get('no_of_re_evaluations')?.valueChanges.subscribe(() => { this.update_re_eval_variance_state(); });

    // Re-validate passing_marks and moderation marks when total_marks changes
    this.form.get('total_marks')?.valueChanges.subscribe(() => {
      this.form.get('passing_marks')?.updateValueAndValidity();
      this.update_condition_validators();
      this.update_re_eval_condition_validators();
    });

    // Re-validate hide pages when total pages change
    this.form.get('no_of_pages_per_script')?.valueChanges.subscribe(() => {
      const ctrl = this.form.get('hide_script_pages_for_faculty');
      ctrl?.updateValueAndValidity();
      if (ctrl?.value) ctrl?.markAsDirty();
    });
    this.form.get('no_of_pages_per_supplement')?.valueChanges.subscribe(() => {
      const ctrl = this.form.get('hide_supplement_pages_for_faculty');
      ctrl?.updateValueAndValidity();
      if (ctrl?.value) ctrl?.markAsDirty();
    });

    // Disable % to hide when pages to hide is 0
    this.form.get('hide_script_pages_for_faculty')?.valueChanges.subscribe((val: any) => {
      const pct_ctrl = this.form.get('hide_script_percentage');
      if (Number(val) === 0) {
        pct_ctrl?.setValue('');
        pct_ctrl?.disable();
      } else {
        pct_ctrl?.enable();
      }
    });
    this.form.get('hide_supplement_pages_for_faculty')?.valueChanges.subscribe((val: any) => {
      const pct_ctrl = this.form.get('hide_supplement_percentage');
      if (Number(val) === 0) {
        pct_ctrl?.setValue('');
        pct_ctrl?.disable();
      } else {
        pct_ctrl?.enable();
      }
    });
  }
  async ngOnInit() {
    this.ar.queryParams.subscribe(async (params: any) => {
      this.params = { ...params }
      if (this.params.id) {
        this.patch_promise = this.patch_form_data(this.params.id)
        if (this.params.action == 'view') {
          this.form.disable();
        } else {
          this.form.enable();
        }
      }
    })
    this.ds.refresh_dropdown_list(['university', 'program', 'stream', 'course', 'subject', 'academic-year', 'college', 'exam', 'level']);
    await this.ds?.get_dropdown_list(['university', 'program', 'stream', 'course', 'subject', 'academic-year', 'college', 'exam', 'level']);
    // On edit/view the assessment loads in parallel with the dropdown lists above. Wait for it so
    // the derived list filtering and directory are built from the patched values, not empty ones.
    await this.patch_promise;
    const university_id = this.form.get('university_id')?.value;
    this.update_exam_and_subject_lists(university_id);
    // The university_id subscription above can't do this on its own during patch_form_data: it
    // fires before `type` is patched (patchValue applies controls in declaration order) and
    // possibly before university_list has loaded. Reconcile once here, when both are settled.
    this.sync_assessment_type();
    this.initial_load_done = true;
    // Directory is composed from dropdown item codes, which only exist once the lists have loaded.
    this.update_directory();
    this.form.get('exam_id')?.valueChanges.subscribe((examId: any) => {
      this.onExamChange(examId);
    });
    this.form.get('subject_id')?.valueChanges.subscribe(() => {
      this.update_directory();
    });
  }

  he_is_required(isRequired: boolean = true) {
    if (isRequired) {
      const current_type = this.form.get('moderation_type')?.value || 'Default';
      if (!this.form.get('moderation_type')?.value) {
        this.form.get('moderation_type')?.setValue('Default');
      }
      this.on_moderation_type_change(current_type);
    } else {
      this.form.get('script_he_to_check')?.clearValidators();
      this.form.get('script_he_to_check')?.setValue('');
      this.form.get('script_he_to_check')?.updateValueAndValidity();
      this.clear_condition_validators();
    }
  }

  on_moderation_type_change(type: string) {
    if (!this.form.get('head_examiner_required')?.value) return;
    if (type === 'Default') {
      this.form.get('script_he_to_check')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      this.form.get('script_he_to_check')?.updateValueAndValidity();
      this.clear_condition_validators();
    } else {
      this.form.get('script_he_to_check')?.clearValidators();
      this.form.get('script_he_to_check')?.setValue('');
      this.form.get('script_he_to_check')?.updateValueAndValidity();
      this.update_condition_validators();
    }
  }

  update_condition_validators() {
    const cfg = this.form.get('moderation_config') as FormGroup;
    const total = Number(this.form.get('total_marks')?.value || 0);
    const conditions = ['low_score', 'high_score', 'variance', 'question_variance'];
    conditions.forEach(f => {
      const enabled = cfg.get(`${f}_enabled`)?.value;
      const marks_ctrl = cfg.get(`${f}_marks`);
      if (enabled) {
        marks_ctrl?.enable();
        const validators: any[] = [Validators.required, Validators.min(0)];
        if ((f === 'low_score' || f === 'high_score') && total > 0) validators.push(Validators.max(total));
        // Question-Wise Variance is a percentage (avg |diff|/max across questions × 100), not raw marks.
        if (f === 'question_variance') validators.push(Validators.max(100));
        marks_ctrl?.setValidators(validators);
      } else {
        marks_ctrl?.clearValidators();
        marks_ctrl?.setValue(null);
        marks_ctrl?.disable();
      }
      marks_ctrl?.updateValueAndValidity();
    });
    cfg.updateValueAndValidity();
  }

  clear_condition_validators() {
    const cfg = this.form.get('moderation_config') as FormGroup;
    ['low_score_marks', 'high_score_marks', 'variance_marks', 'question_variance_marks'].forEach(f => {
      const ctrl = cfg.get(f);
      ctrl?.clearValidators();
      ctrl?.setValue(null);
      ctrl?.disable();
      ctrl?.updateValueAndValidity();
    });
  }

  update_variance_state() {
    const cfg = this.form.get('moderation_config') as FormGroup;
    if (this.is_variance_disabled) {
      cfg.get('variance_enabled')?.setValue(false);
      cfg.get('variance_marks')?.setValue(null);
    }
    if (this.is_question_variance_disabled) {
      cfg.get('question_variance_enabled')?.setValue(false);
      cfg.get('question_variance_marks')?.setValue(null);
    }
  }

  // Level Variance / Question Wise Variance both need a comparison source: >=2 evaluator
  // levels compares last two levels (no AI needed — enabled regardless of the AI Evaluation
  // toggle), exactly 1 level compares against the AI score — which only exists when AI
  // Evaluation is on. Disabled for AI Only (no evaluator level at all).
  get is_variance_disabled(): boolean {
    if (this.is_ai_only) return true;
    // if (this.form.get('ai_evaluation')?.value !== 'enable') return true;
    const no_of_eval = Number(this.form.get('no_of_evaluation')?.value);
    if (!no_of_eval || no_of_eval < 1) return true;
    if (no_of_eval >= 2) return false;
    return this.form.get('ai_evaluation')?.value !== 'enable';
  }

  get is_question_variance_disabled(): boolean {
    return this.is_variance_disabled;
  }

  // AI-only assessments have no faculty at all — Per Day Target, Minimum Time Per
  // Answer Script and Grace Period are all faculty-timing settings that don't apply.
  update_ai_only_field_validators() {
    const time_fields = ['grace_period', 'min_time_per_answer_script'];
    const target_ctrl = this.form.get('per_day_target');
    if (this.is_ai_only) {
      target_ctrl?.clearValidators();
      target_ctrl?.setValue('');
      target_ctrl?.disable();
      time_fields.forEach(f => {
        const ctrl = this.form.get(f);
        ctrl?.clearValidators();
        ctrl?.setValue('');
        ctrl?.disable();
      });
    } else {
      target_ctrl?.setValidators([Validators.required, Validators.min(1)]);
      target_ctrl?.enable();
      time_fields.forEach(f => {
        const ctrl = this.form.get(f);
        ctrl?.setValidators([Validators.required, this.notZeroTimeValidator.bind(this)]);
        ctrl?.enable();
      });
    }
    target_ctrl?.updateValueAndValidity();
    time_fields.forEach(f => this.form.get(f)?.updateValueAndValidity());
  }

  get is_configurable(): boolean {
    return this.form.get('head_examiner_required')?.value === true && this.form.get('moderation_type')?.value === 'Configurable';
  }

  updateReEvaluationValidators(isRequired: boolean) {
    const fields = ['re_evaluation_start_date', 're_evaluation_end_date', 're_evaluation_last_date', 're_evaluation_fees'];

    if (isRequired) {
      fields.forEach(field => {
        this.form.get(field)?.setValidators([Validators.required]);
        this.form.get(field)?.updateValueAndValidity();
      });
      const no_of_re_eval_ctrl = this.form.get('no_of_re_evaluations');
      no_of_re_eval_ctrl?.setValidators([Validators.required]);
      if (no_of_re_eval_ctrl?.value === null || no_of_re_eval_ctrl?.value === '' || no_of_re_eval_ctrl?.value === undefined) {
        no_of_re_eval_ctrl?.setValue(1);
      }
      no_of_re_eval_ctrl?.updateValueAndValidity();
      // Trigger amount validation check based on current fees value
      this.updateAmountValidator(this.form.get('re_evaluation_fees')?.value);
    } else {
      fields.forEach(field => {
        this.form.get(field)?.clearValidators();
        this.form.get(field)?.setValue('');
        this.form.get(field)?.updateValueAndValidity();
      });
      // Also clear amount validator
      this.form.get('amount')?.clearValidators();
      this.form.get('amount')?.setValue(null);
      this.form.get('amount')?.updateValueAndValidity();
      // Clear HE fields
      this.form.get('no_of_re_evaluations')?.clearValidators();
      this.form.get('no_of_re_evaluations')?.setValue(null);
      this.form.get('no_of_re_evaluations')?.updateValueAndValidity();
      this.form.get('re_evaluation_he_required')?.setValue(false);
      this.form.get('re_evaluation_script_he_to_check')?.clearValidators();
      this.form.get('re_evaluation_script_he_to_check')?.setValue('');
      this.form.get('re_evaluation_script_he_to_check')?.updateValueAndValidity();
    }
  }

  update_model_answer_sheet_validator() {
    const ctrl = this.form.get('model_answer_sheet');
    if (this.model_answer_sheet_required) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity();
  }

  updateReEvalHeValidator(isRequired: boolean) {
    if (isRequired) {
      const current_type = this.form.get('re_evaluation_moderation_type')?.value || 'Default';
      if (!this.form.get('re_evaluation_moderation_type')?.value) {
        this.form.get('re_evaluation_moderation_type')?.setValue('Default');
      }
      this.on_re_eval_moderation_type_change(current_type);
    } else {
      this.form.get('re_evaluation_script_he_to_check')?.clearValidators();
      this.form.get('re_evaluation_script_he_to_check')?.setValue('');
      this.form.get('re_evaluation_script_he_to_check')?.updateValueAndValidity();
      this.clear_re_eval_condition_validators();
    }
  }

  on_re_eval_moderation_type_change(type: string) {
    if (!this.form.get('re_evaluation_he_required')?.value) return;
    if (type === 'Default') {
      this.form.get('re_evaluation_script_he_to_check')?.setValidators([Validators.required, Validators.min(1), Validators.max(100)]);
      this.form.get('re_evaluation_script_he_to_check')?.updateValueAndValidity();
      this.clear_re_eval_condition_validators();
    } else {
      this.form.get('re_evaluation_script_he_to_check')?.clearValidators();
      this.form.get('re_evaluation_script_he_to_check')?.setValue('');
      this.form.get('re_evaluation_script_he_to_check')?.updateValueAndValidity();
      this.update_re_eval_condition_validators();
    }
  }

  update_re_eval_condition_validators() {
    const cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    const total = Number(this.form.get('total_marks')?.value || 0);
    const conditions = ['low_score', 'high_score', 'variance', 'question_variance'];
    conditions.forEach(f => {
      const enabled = cfg.get(`${f}_enabled`)?.value;
      const marks_ctrl = cfg.get(`${f}_marks`);
      if (enabled) {
        marks_ctrl?.enable();
        const validators: any[] = [Validators.required, Validators.min(0)];
        if ((f === 'low_score' || f === 'high_score') && total > 0) validators.push(Validators.max(total));
        // Question-Wise Variance is a percentage (avg |diff|/max across questions × 100), not raw marks.
        if (f === 'question_variance') validators.push(Validators.max(100));
        marks_ctrl?.setValidators(validators);
      } else {
        marks_ctrl?.clearValidators();
        marks_ctrl?.setValue(null);
        marks_ctrl?.disable();
      }
      marks_ctrl?.updateValueAndValidity();
    });
    cfg.updateValueAndValidity();
  }

  clear_re_eval_condition_validators() {
    const cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    ['low_score_marks', 'high_score_marks', 'variance_marks', 'question_variance_marks'].forEach(f => {
      const ctrl = cfg.get(f);
      ctrl?.clearValidators();
      ctrl?.setValue(null);
      ctrl?.disable();
      ctrl?.updateValueAndValidity();
    });
  }

  update_re_eval_variance_state() {
    const cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    if (this.is_re_eval_variance_disabled) {
      cfg.get('variance_enabled')?.setValue(false);
      cfg.get('variance_marks')?.setValue(null);
    }
    if (this.is_re_eval_question_variance_disabled) {
      cfg.get('question_variance_enabled')?.setValue(false);
      cfg.get('question_variance_marks')?.setValue(null);
    }
  }

  // Same comparison-source rule as the main Evaluation's Level/Question Wise Variance:
  // >=2 re-evaluation levels compares last two levels (no AI needed), exactly 1 level
  // compares against the AI score — which only exists when AI Evaluation is on.
  get is_re_eval_variance_disabled(): boolean {
    const no_of_re_eval = Number(this.form.get('no_of_re_evaluations')?.value);
    if (!no_of_re_eval || no_of_re_eval < 1) return true;
    if (no_of_re_eval >= 2) return false;
    return this.form.get('ai_evaluation')?.value !== 'enable';
  }

  get is_re_eval_question_variance_disabled(): boolean {
    return this.is_re_eval_variance_disabled;
  }

  submitted = false;

  get has_configurable_condition(): boolean {
    const cfg = this.form.get('moderation_config') as FormGroup;
    return !!(cfg.get('low_score_enabled')?.value || cfg.get('high_score_enabled')?.value || cfg.get('variance_enabled')?.value || cfg.get('question_variance_enabled')?.value);
  }

  get has_re_eval_configurable_condition(): boolean {
    const cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    return !!(cfg.get('low_score_enabled')?.value || cfg.get('high_score_enabled')?.value || cfg.get('variance_enabled')?.value || cfg.get('question_variance_enabled')?.value);
  }

  get mark_range_invalid(): boolean {
    const cfg = this.form.get('moderation_config') as FormGroup;
    if (!cfg.get('low_score_enabled')?.value || !cfg.get('high_score_enabled')?.value) return false;
    const low = cfg.get('low_score_marks')?.value;
    const high = cfg.get('high_score_marks')?.value;
    if (low == null || high == null) return false;
    return Number(high) < Number(low);
  }

  get re_eval_mark_range_invalid(): boolean {
    const cfg = this.form.get('re_evaluation_moderation_config') as FormGroup;
    if (!cfg.get('low_score_enabled')?.value || !cfg.get('high_score_enabled')?.value) return false;
    const low = cfg.get('low_score_marks')?.value;
    const high = cfg.get('high_score_marks')?.value;
    if (low == null || high == null) return false;
    return Number(high) < Number(low);
  }

  updateAmountValidator(hasFees: boolean) {
    const amountControl = this.form.get('amount');
    const isReEvalRequired = this.form.get('re_evaluation_required')?.value;

    if (hasFees && isReEvalRequired) {
      amountControl?.setValidators([Validators.required]);
    } else {
      amountControl?.clearValidators();
      if (!hasFees || !isReEvalRequired) {
        amountControl?.setValue(0);
      }
    }
    amountControl?.updateValueAndValidity();
  }

  on_university_change(universityId: any) {
    this.form.patchValue({
      exam_id: null,
      academic_year_id: null,
      college_id: null,
      level_id: null,
      program_id: null,
      stream_id: null,
      subject_id: null,
    }, { emitEvent: false });
    this.directory = '';
    this.used_subject_ids = [];
    this.update_exam_and_subject_lists(universityId);
  }

  update_exam_and_subject_lists(universityId: any) {
    const all_subjects = this.ds.dropdown_list?.subject_list || [];
    // Exam name/code options stay the full list whatever is selected. Narrowing them to the picked
    // exam's university made the dropdown shrink after every selection and left no way to switch
    // straight to an exam from a different university without clearing the field first.
    this.filtered_exam_list = this.ds.dropdown_list?.exam_name_and_code_list || [];
    this.filtered_exam_code_list = this.ds.dropdown_list?.exam_code_list || [];
    // Subjects stay scoped to the exam's university — a subject from another university can't be
    // assessed under this exam.
    this.available_subject_list = universityId ? all_subjects.filter((s: any) => s.university_id == universityId).map((s: any) => ({ title: s.name, value: s.id, code: s.code, university_id: s.university_id })) : [];
    this.apply_used_subject_filter();
  }

  // Excludes subjects that already have an assessment created against the
  // currently selected exam, so the same subject can't be picked twice for
  // one exam. When editing, the assessment's own subject stays selectable.
  async refresh_used_subjects(examId: number) {
    if (!examId) {
      this.used_subject_ids = [];
      return;
    }
    try {
      const res = await this.as.get_list({ exam_id: examId, page_size: 999999999 });
      const exclude_id = this.params?.id ? Number(this.params.id) : null;
      this.used_subject_ids = (res?.data || [])
        .filter((a: any) => a.subject_id != null && a.id !== exclude_id)
        .map((a: any) => a.subject_id);
    } catch (error) {
      console.error('Error fetching assessments for exam:', error);
      this.used_subject_ids = [];
    }
    this.apply_used_subject_filter();
  }

  apply_used_subject_filter() {
    if (this.used_subject_ids.length) {
      this.available_subject_list = this.available_subject_list.filter((s: any) => !this.used_subject_ids.includes(s.value));
    }
  }

  async onExamChange(examId: number) {
    if (!examId) {
      this.form.patchValue({
        exam_id: null,
        university_id: null,
        academic_year_id: null,
        college_id: null,
        level_id: null,
        program_id: null,
        stream_id: null,
        subject_id: null,
        image_to_text: 'disable',
        ai_proctoring: 'disable',
        ai_evaluation: 'disable',
      }, { emitEvent: false });
      this.directory = '';
      this.used_subject_ids = [];
      this.update_exam_and_subject_lists(null);
      return;
    }
    const examList = this.ds.dropdown_list?.exam_list;
    if (!examList || !examList.length) return;

    const selectedExam = examList.find((e: any) => e?.id == examId);
    if (!selectedExam) return;
    this.form.patchValue({
      exam_id: selectedExam.id,
      academic_year_id: selectedExam.academic_year_id,
      university_id: selectedExam.university_id,
      college_id: selectedExam.college_id,
      level_id: selectedExam.level_id,
      program_id: selectedExam.program_id,
      stream_id: selectedExam.stream_id,
      subject_id: null,
    }, { emitEvent: false });
    this.update_exam_and_subject_lists(selectedExam.university_id);
    await this.refresh_used_subjects(examId);
    this.directory = '';
  }

  async patch_form_data(id: any) {
    try {
      let response = await this.as.get(id, {})
      let patch_data = { ...response.data };
      this.is_ai_features_locked = patch_data.ai_features_locked === true;
      const subject_id = patch_data.subject_id;
      this.form.patchValue({ ...patch_data });
      // Explicitly trigger validator updates — patchValue won't fire valueChanges
      // if re_evaluation_required is already false (matches default)
      this.updateReEvaluationValidators(this.form.get('re_evaluation_required')?.value);
      this.update_model_answer_sheet_validator();

      // Restore rubrics_json into FormArray (patchValue doesn't populate FormArray from plain array)
      const rubrics_array = this.form.get('rubrics_json') as FormArray;
      rubrics_array.clear();
      if (Array.isArray(patch_data.rubrics_json)) {
        patch_data.rubrics_json.forEach((item: any) => {
          rubrics_array.push(
            this.fb.group({
              question_number: [item.question_number],
              optionality: [item.optionality],
              parent: [item.parent],
              marks: [item.marks]
            })
          );
        });
      }

      const re_eval = patch_data.ReEvaluations;
      this.form.get('re_evaluation_start_date')?.setValue(patch_data.re_evaluation_start_date || re_eval?.re_evaluation_start_date || '');
      this.form.get('re_evaluation_end_date')?.setValue(patch_data.re_evaluation_end_date || re_eval?.re_evaluation_end_date || '');
      this.form.get('re_evaluation_last_date')?.setValue(patch_data.re_evaluation_last_date || re_eval?.re_evaluation_last_date || '');
      this.form.get('re_evaluation_fees')?.setValue(patch_data.re_evaluation_fees ?? re_eval?.re_evaluation_fees ?? false);
      this.form.get('amount')?.setValue(patch_data.amount ?? re_eval?.amount ?? null);
      this.form.get('no_of_re_evaluations')?.setValue(patch_data.no_of_re_evaluations ?? re_eval?.no_of_re_evaluations ?? 1);
      this.form.get('re_evaluation_he_required')?.setValue(patch_data.re_evaluation_he_required ?? re_eval?.re_evaluation_he_required ?? false);
      this.form.get('re_evaluation_script_he_to_check')?.setValue(patch_data.re_evaluation_script_he_to_check || re_eval?.re_evaluation_script_he_to_check || '');
      // Patch script config fields from first script of this assessment
      const sc = patch_data.script_config;
      if (sc) {
        this.form.patchValue({
          no_of_pages_per_script: sc.no_of_pages_per_script || '',
          hide_script_pages_for_faculty: sc.hide_script_pages_for_faculty || '',
          hide_script_percentage: sc.hide_script_percentage || '',
          no_of_pages_per_supplement: sc.no_of_pages_per_supplement || '',
          hide_supplement_pages_for_faculty: sc.hide_supplement_pages_for_faculty || '',
          hide_supplement_percentage: sc.hide_supplement_percentage || '',
        });
        if (Number(sc.hide_script_pages_for_faculty) === 0) {
          this.form.get('hide_script_percentage')?.disable();
        }
        if (Number(sc.hide_supplement_pages_for_faculty) === 0) {
          this.form.get('hide_supplement_percentage')?.disable();
        }
      }
      // Patch moderation fields
      this.form.get('moderation_type')?.setValue(patch_data.moderation_type || 'Default');
      if (patch_data.moderation_config) {
        (this.form.get('moderation_config') as FormGroup).patchValue(patch_data.moderation_config);
      }
      this.update_variance_state();
      this.update_ai_only_field_validators();

      // Patch re-evaluation moderation fields
      this.form.get('re_evaluation_moderation_type')?.setValue(re_eval?.re_evaluation_moderation_type || 'Default');
      if (re_eval?.re_evaluation_moderation_config) {
        (this.form.get('re_evaluation_moderation_config') as FormGroup).patchValue(re_eval.re_evaluation_moderation_config);
      }
      this.update_re_eval_variance_state();

      if (subject_id) {
        setTimeout(() => {
          this.form.patchValue({ subject_id }, { emitEvent: false });
          this.update_directory();
        }, 0);
      }
      // Scheduled after the subject_id patch above (same 0ms delay tasks run in registration
      // order) so the snapshot captures the form's fully-settled loaded state.
      setTimeout(() => {
        this.initial_form_snapshot = JSON.stringify(this.form.getRawValue());
      }, 0);
      console.log(response, "jkjkjjkjkjkjkjjkjkjkjkjkj");
    } catch (error) {
      console.log(error);

    }
  }

  get_invalid_controls(group: any = this.form, prefix = ''): string[] {
    const result: string[] = [];
    Object.keys(group.controls || {}).forEach(key => {
      const ctrl = group.get(key);
      if (!ctrl) return;
      if (ctrl.controls) {
        result.push(...this.get_invalid_controls(ctrl, `${prefix}${key}.`));
      } else if (ctrl.invalid && ctrl.enabled) {
        result.push(`${prefix}${key} = ${JSON.stringify(ctrl.value)} | errors: ${JSON.stringify(ctrl.errors)}`);
      }
    });
    return result;
  }

  async submit(item?: any) {
    // A true no-op update (editing an existing assessment, nothing actually changed) should
    // always be allowed through — don't re-run client-side validation the original data might
    // not satisfy (e.g. a validator added after this assessment was created). This mirrors the
    // backend's own "nothing changed → skip validation" short-circuit (update-assessment.function.ts).
    const is_unchanged_update = !!this.params.id && this.initial_form_snapshot !== null
      && this.initial_form_snapshot === JSON.stringify(this.form.getRawValue());

    if (!is_unchanged_update) {
      // Only flip to "submitted" (which also drives template-level error text, e.g. the
      // moderation-condition warnings below) when validation is actually going to run —
      // otherwise a no-op update on legacy-invalid data would still flash those errors.
      this.submitted = true;
      console.log('FORM VALUE:', this.form.value);
      console.log('FORM VALID:', this.form.valid, '| INVALID CONTROLS:', this.get_invalid_controls());
      if (this.form.invalid) {
        console.warn('INVALID CONTROLS:', this.get_invalid_controls());
        this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
        this.form.markAllAsTouched();
        return;
      }
      const fv = this.form.getRawValue();
      if (fv.head_examiner_required && fv.moderation_type === 'Configurable' && !this.has_configurable_condition) {
        this.gs.toastr_shows_function('Please select at least one moderation condition', 'Error', 'error');
        return;
      }

      if (fv.re_evaluation_he_required && fv.re_evaluation_moderation_type === 'Configurable' && !this.has_re_eval_configurable_condition) {
        this.gs.toastr_shows_function('Please select at least one re-evaluation moderation condition', 'Error', 'error');
        return;
      }

      // Check if rubrics CSV has been uploaded
      const rubrics_array_check = this.form.get('rubrics_json') as FormArray;
      if (!rubrics_array_check || rubrics_array_check.length === 0) {
        this.gs.toastr_shows_function('The uploaded CSV file is empty. Please upload a valid Rubrics file.', 'Error', 'error');
        return;
      }

      // Check if rubric marks match total_marks before submitting
      const exact_marks = this.calculate_exact_marks(rubrics_array_check.value);
      const form_total_marks = Number(this.form.get('total_marks')?.value || 0);
      if (exact_marks !== form_total_marks) {
        this.gs.toastr_shows_function(`The total rubric marks (${exact_marks}) does not match the total marks (${form_total_marks}) entered.`, 'Error', 'error');
        return;
      }
    }

    // this.fs.is_loading = true;
    let form_value = this.form.getRawValue();
    form_value.total_marks = +form_value.total_marks
    form_value.passing_marks = +form_value.passing_marks
    form_value.no_of_re_evaluations = form_value.no_of_re_evaluations !== '' && form_value.no_of_re_evaluations !== null ? +form_value.no_of_re_evaluations : 1
    form_value.amount = form_value.amount !== '' && form_value.amount !== null ? +form_value.amount : null
    form_value.re_evaluation_script_he_to_check = form_value.re_evaluation_script_he_to_check !== '' && form_value.re_evaluation_script_he_to_check !== null ? +form_value.re_evaluation_script_he_to_check : null

    if (!this.show_image_to_text) {
      form_value.image_to_text = 'disable';
    }
    if (!this.show_ai_proctoring) {
      form_value.ai_proctoring = 'disable';
    }
    if (!this.show_ai_evaluation) {
      form_value.ai_evaluation = 'disable';
    }

    // Moderation payload cleanup
    if (!form_value.head_examiner_required) {
      form_value.moderation_type = 'Default';
      form_value.moderation_config = null;
    } else if (form_value.moderation_type === 'Default') {
      form_value.moderation_config = null;
    } else if (form_value.moderation_type === 'Configurable') {
      form_value.script_he_to_check = null;
    }

    // Re-evaluation moderation payload cleanup
    if (!form_value.re_evaluation_he_required) {
      form_value.re_evaluation_moderation_type = 'Default';
      form_value.re_evaluation_moderation_config = null;
    } else if (form_value.re_evaluation_moderation_type === 'Default') {
      form_value.re_evaluation_moderation_config = null;
    } else if (form_value.re_evaluation_moderation_type === 'Configurable') {
      form_value.re_evaluation_script_he_to_check = null;
    }

    try {
      if (this.params.id) {
        let update_response = await this.as.update(this.params.id, form_value);
        this.gs.toastr_shows_function('Changes updated successfully', 'Success', 'success');
      } else {
        let create_response = await this.as.add(form_value);
        this.gs.toastr_shows_function(create_response?.message || 'Assessment created', 'Success', 'success');
      }
      this.route.navigate(['/assessment/list']);
    } catch (error: any) {
      // Show server validation / duplicate messages to user via toast
      const errMsg = error?.error?.message || error?.message || 'Something went wrong';
      this.gs.toastr_shows_function(errMsg, 'Error', 'error');
    } finally {
      // this.fs.is_loading = false;
    }
  }
  update_directory() {
    const exam_id = this.form.get('exam_id')?.value;
    const subject_id = this.form.get('subject_id')?.value;
    const academic_year_id = this.form.get('academic_year_id')?.value;
    const university_id = this.form.get('university_id')?.value;
    const college_id = this.form.get('college_id')?.value;
    const level_id = this.form.get('level_id')?.value;
    const program_id = this.form.get('program_id')?.value;
    const stream_id = this.form.get('stream_id')?.value;

    if (!exam_id || !subject_id) {
      this.directory = '';
      return;
    }

    const examItem = this.ds.dropdown_list?.exam_list?.find((x: any) => x.value == exam_id);
    const subjectItem = this.ds.dropdown_list?.subject_list?.find((x: any) => x.value == subject_id);
    const academicItem = this.ds.dropdown_list?.academic_list?.find((x: any) => x.value == academic_year_id);
    const universityItem = this.ds.dropdown_list?.university_list?.find((x: any) => x.value == university_id);
    const collegeItem = this.ds.dropdown_list?.college_list?.find((x: any) => x.value == college_id);
    const levelItem = this.ds.dropdown_list?.level_list?.find((x: any) => x.value == level_id);
    const programItem = this.ds.dropdown_list?.program_list?.find((x: any) => x.value == program_id);
    const streamItem = this.ds.dropdown_list?.stream_list?.find((x: any) => x.value == stream_id);

    const academic_year_name = academicItem?.name || '';
    const university_code = universityItem?.code || '';
    const college_code = collegeItem?.code || '';
    const level_code = levelItem?.code || '';
    const program_code = programItem?.code || '';
    const stream_code = streamItem?.code || '';
    const exam_code = examItem?.code || '';
    const subject_code = subjectItem?.code || '';

    if (academic_year_name && university_code && exam_code && subject_code) {
      this.directory = `${academic_year_name}/${university_code}/${college_code}/${level_code}/${program_code}/${stream_code}/${exam_code}/${subject_code}`;
    } else {
      this.directory = '';
    }
  }

  copy_directory() {
    if (this.directory) {
      navigator.clipboard.writeText(this.directory);
      this.directory_copied = true;
      setTimeout(() => { this.directory_copied = false; }, 2000);
    }
  }

  calculate_exact_marks(formatted_data: any[]): number {
    const childrenMap: { [key: string]: any[] } = {};
    const rootNodes: any[] = [];

    formatted_data.forEach(item => {
      const parentId = item.parent ? String(item.parent).trim() : '';
      if (parentId) {
        if (!childrenMap[parentId]) {
          childrenMap[parentId] = [];
        }
        childrenMap[parentId].push(item);
      } else {
        rootNodes.push(item);
      }
    });

    const get_node_marks = (node: any): number => {
      const qNum = String(node.question_number).trim();
      const children = childrenMap[qNum] || [];

      if (children.length === 0) {
        return Number(node.marks || 0);
      }

      const childMarks = children.map(child => get_node_marks(child));
      childMarks.sort((a, b) => b - a);

      const opt = Number(node.optionality || 0);
      let marksToTake = childMarks.length;
      if (opt > 0 && opt < childMarks.length) {
        marksToTake = opt;
      }

      let total = 0;
      for (let i = 0; i < marksToTake; i++) {
        total += childMarks[i];
      }
      return total;
    };

    let total_paper_marks = 0;
    rootNodes.forEach(root => {
      total_paper_marks += get_node_marks(root);
    });

    // If no root nodes exist (CSV has only child rows, no explicit parent rows),
    // treat each orphan group as fully mandatory — no optionality constraint
    if (rootNodes.length === 0) {
      const processedParents = new Set<string>();
      rootNodes.forEach(r => processedParents.add(String(r.question_number).trim()));
      Object.entries(childrenMap).forEach(([parentId, children]) => {
        if (!processedParents.has(parentId)) {
          children.forEach(c => {
            total_paper_marks += Number(c.marks || 0);
          });
        }
      });
    }
    return total_paper_marks;
  }

  after_file_upload(parsed_file_data: File) {
    if (!Array.isArray(parsed_file_data)) return;

    const formatted_data = this.convert_to_question_objects(parsed_file_data);
    // Filter out rows where question_number is empty (blank rows / header-only CSV)
    const valid_data = formatted_data.filter((item: any) =>
      item.question_number && String(item.question_number).trim() !== '' && String(item.question_number).trim() !== 'undefined'
    );

    if (valid_data.length === 0) {
      this.gs.toastr_shows_function('The uploaded CSV file is empty. Please upload a valid Rubrics file.', 'Error', 'error');
      const rubrics_array = this.form.get('rubrics_json') as FormArray;
      rubrics_array?.clear();
      return;
    }

    const rubrics_array = this.form.get('rubrics_json') as FormArray;
    rubrics_array.clear();

    valid_data.forEach((item: any) => {
      rubrics_array.push(
        this.fb.group({ question_number: [item.question_number], optionality: [item.optionality], parent: [item.parent], marks: [item.marks] })
      );
    });

    const exact_marks = this.calculate_exact_marks(valid_data);
    const form_total_marks = Number(this.form.get('total_marks')?.value || 0);

    if (exact_marks !== form_total_marks) {
      this.gs.toastr_shows_function(`The total rubric marks (${exact_marks}) does not match the total marks (${form_total_marks}) entered.`, 'Error', 'error');
    }
    console.log('Rubrics patched:', rubrics_array.value);
  }

  convert_to_question_objects(rows: any) {
    return rows.map((row: any) => {
      return {
        question_number: String(row["Question Numbers"] ?? row.question_number),
        optionality: Number(row["Optionality"] ?? row.optionality ?? 0),
        parent: row["Parent"] || row.parent || null,
        marks: Number(row["Marks"] ?? row.marks ?? 0)
      }
    });
  }
}
